import React, { useCallback, useEffect, useState } from 'react'
// import { Checkbox, FormControlLabel } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import {
  CustomerTiers,
  dashboardUrl,
  DOMAIN_NAME,
  forgotPasswordUrl,
  loginWithSsoUrl,
  setCookieUrl,
  opencloudHomeUrl,
  loginUrl,
  appUrlRoot,
} from 'app/constants'
import { LoginMethodTypes } from 'app/plugins/account/components/userManagement/users/helpers'
import { authenticateUser } from 'app/plugins/account/components/userManagement/users/new-actions'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import { MessageTypes } from 'core/components/notifications/model'
import SimpleLink from 'core/components/SimpleLink'
import { loadFeatures } from 'core/containers/AppController'
import FormPageContainer from 'core/containers/FormPageContainer'
import Button from 'core/elements/button'
import Checkbox from 'core/elements/input/Checkbox'
import Input from 'core/elements/input/Input'
import { topMiddle } from 'core/elements/menu/defaults'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import { useToast } from 'core/providers/ToastProvider'
import { PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { getProductName, getSSORedirectUrl, isDefaultDomain, isOpenCloud } from 'core/utils/helpers'
import Cookies from 'js-cookie'
import moment from 'moment'
import { isEmpty, prop } from 'ramda'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import Bugsnag from 'utils/bugsnag'
import generateTestId from 'utils/test-helpers'
import { trackEvent } from 'utils/tracking'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import { getAppBasePath, isDevelopment } from 'core/utils/helpers'
import Progress from 'pf9-ui-components/built/components/progress/Progress'
import { mfaDoc } from 'app/docLinks'

const useStyles = makeStyles((theme: Theme) => ({
  methodToggle: {
    position: 'absolute',
    top: 17,
    right: 26,
    color: theme.palette.blue[500],
    cursor: 'pointer',
  },
  form: {
    maxWidth: 400,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 24,
  },
  paragraph: {
    textAlign: 'center',
  },
  signinButton: {
    marginTop: 16,
    width: '100%',
  },
  forgotPwd: {
    alignSelf: 'end',
    lineHeight: '18px',
    marginTop: 8,
  },
  formTitle: {
    textAlign: 'center',
  },
  passwordInputField: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& .input[type=password]::-ms-reveal, & .input[type=password]::-ms-clear': {
      display: 'none',
    },
  },
  mfa: {
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'start',
    gap: 4,
    '& p': {
      lineHeight: '16px',
    },
  },
  mfaInfo: {
    '& > span': {
      lineHeight: '16px',
    },
  },
  mfaContainer: {
    marginBottom: theme.spacing(),
    textAlign: 'center',
  },
  fields: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'start',
    gap: 16,
  },
  loginMethods: {
    display: 'flex',
    marginTop: theme.spacing(3),
  },
  loginMethod: {
    flexGrow: 1,
    textTransform: 'uppercase',
    textAlign: 'center',
    color: theme.palette.grey['000'],
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.grey['000'],
    '&.active': {
      borderBottomWidth: 3,
    },
    '&.inactive': {
      borderBottomWidth: 1,
      color: theme.palette.grey[500],
      marginBottom: 1,
      cursor: 'pointer',
    },
  },
  progress: {
    '& img': {
      height: 130,
    },
    '& img ~ span': {
      fontSize: theme.typography.subtitle1.fontSize,
    },
  },
}))

interface Props {
  onAuthSuccess: any
  ssoEnabled: boolean
  customerTier: string
  infraHostUrl: string
}

const defaultParams = {
  username: '',
  password: '',
  domain: 'default',
  loginMethod: LoginMethodTypes.Local,
  MFAcheckbox: false,
  mfa: '',
  loginFailed: false,
  loading: false,
  ssoEnabled: true,
}

const LoginPage: React.FC<Props> = (props) => {
  const classes = useStyles({})
  const showToast = useToast()

  // const { ssoEnabled, onAuthSuccess, customerTier } = props
  const { onAuthSuccess, customerTier, infraHostUrl } = props
  const ssoEnabled = true
  const { history, location } = useReactRouter()
  const { getUserPrefs } = useScopedPreferences()
  const [params, setParams] = useState(defaultParams)
  const [showPassword, setShowPassword] = useState(false)
  const [openForgetPassModal, setOpenForgetPassModal] = useState(false)
  const preferences = useSelector(prop<string, PreferencesState>(preferencesStoreKey))
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features, username } = session
  const intendedDestination = location.state?.from
  const appBasePath = getAppBasePath()
  const hasIntendedDestination =
    !!intendedDestination &&
    !['/', appBasePath, appUrlRoot, loginUrl, `${appUrlRoot}/index.html`].includes(
      intendedDestination,
    )
  const shouldAutoRedirectToSsoLogin = isOpenCloud() && hasIntendedDestination && !isDevelopment()

  const updateParams = (p) => {
    setParams((params) => {
      return { ...params, ...p }
    })
  }
  const {
    username: loginUsername,
    password,
    loginMethod,
    MFAcheckbox,
    mfa,
    loginFailed,
    loading,
    domain,
  } = params

  useEffect(() => {
    if (ssoEnabled) {
      updateParams({ loginMethod: LoginMethodTypes.SSO })
    }

    // Check if features are loaded in the session
    if (isEmpty(features)) {
      const getFeatures = async () => {
        await loadFeatures()
      }

      getFeatures()
    }
  }, [])

  const setDomainNameCookie = (domain: string) => {
    Cookies.remove(DOMAIN_NAME) // Clear any existing cookie
    Cookies.set(DOMAIN_NAME, domain) // Set new cookie
  }

  // Redirect to SSO login
  const redirectToSsoLogin = () => {
    const appBasePath = getAppBasePath()
    const ssoRedirectUrl = getSSORedirectUrl(domain)
    const originHost = window.location.origin
    if (originHost === infraHostUrl) {
      if (!isDefaultDomain(domain)) setDomainNameCookie(domain)
      window.location.href = `${appBasePath}${ssoRedirectUrl}`
    } else {
      const url = new URL(
        `${infraHostUrl}${appBasePath}${setCookieUrl}?redirectUrl=${originHost}${appBasePath}${loginWithSsoUrl}&domain=${domain}`,
      )
      window.location.replace(url)
    }
  }

  const updateValue = (key) => (event) => {
    updateParams({ [key]: event.target.value })
  }

  const handleLoginError = () => {
    // If SSO login fails, user is redirected so don't show the alert
    if (loginMethod === LoginMethodTypes.SSO) {
      return
    }
    return updateParams({ loading: false, loginFailed: true })
  }

  const performLogin = useCallback(
    async (event) => {
      event.preventDefault()
      updateParams({ loginFailed: false, loading: true })
      const isDefaultDomainName = isDefaultDomain(domain)

      // If user enters a domain other than defaul, set it in the cookie
      if (!isDefaultDomainName) {
        setDomainNameCookie(domain)
      }

      Bugsnag.leaveBreadcrumb('Attempting PF9 Sign In', {
        loginUsername,
        loginMethod,
        MFAcheckbox,
        mfa,
      })

      const { username, unscopedToken, expiresAt, issuedAt, isSsoToken } = await authenticateUser({
        loginUsername,
        password,
        loginMethod,
        MFAcheckbox,
        mfa,
        ...(!isDefaultDomainName && { domain }),
      })

      const lastVisitedPath = preferences?.[username]?.root?.lastVisitedPath

      // If login fails, show error message
      if (!unscopedToken) {
        return handleLoginError()
      }

      // Track successful login
      trackEvent('PF9 Signed In', {
        username,
        duDomain: window.location.origin,
      })
      const { currentTenant, currentRegion } = getUserPrefs(username)

      // Trigger authentication success logic
      await onAuthSuccess({
        currentTenant,
        currentRegion,
        username,
        password,
        unscopedToken,
        expiresAt,
        issuedAt,
        isSsoToken,
        ...(!isDefaultDomainName && { domain }),
      })

      updateParams({ loading: false })

      const defaultUrl = isOpenCloud() ? opencloudHomeUrl : dashboardUrl
      // Use intended destination if available, otherwise fall back to lastVisitedPath or default URL
      const redirectPath = intendedDestination || lastVisitedPath || defaultUrl
      history.push(redirectPath)
    },
    [
      onAuthSuccess,
      loginUsername,
      password,
      loginMethod,
      MFAcheckbox,
      mfa,
      domain,
      isOpenCloud,
      preferences,
      username,
      intendedDestination,
    ],
  )

  const handleChangeBox = (name) => (checked) => {
    updateParams({ [name]: checked })
  }

  const handleForgotPassword = useCallback(
    () => (e) => {
      if (!features?.experimental) {
        showToast('Still loading, please try refreshing the page.', MessageTypes.warning, 3000, {
          vertical: 'top',
          horizontal: 'center',
        })
        return
      }
      features?.experimental?.airgapped
        ? setOpenForgetPassModal(true)
        : history.push(forgotPasswordUrl)
    },
    [features, showToast],
  )

  useEffect(() => {
    const params = new URLSearchParams(history.location.search)
    const ssoLoginError = params.get('ssoLoginError')

    if (ssoLoginError === 'true') {
      showToast(
        `This account is not authorized to access ${getProductName()}. For more information please contact your admin.`,
        MessageTypes.error,
        5000,
        {
          vertical: 'top',
          horizontal: 'center',
        },
      )
    }
  }, [showToast])

  useEffect(() => {
    // Store the intended destination in session storage if SSO is selected
    // This is used to redirect the user to the intended destination after SSO login
    if (loginMethod === LoginMethodTypes.SSO) {
      sessionStorage.setItem('targetPath', intendedDestination)
    } else {
      sessionStorage.removeItem('targetPath')
    }
  }, [loginMethod, intendedDestination])

  useEffect(() => {
    if (shouldAutoRedirectToSsoLogin && infraHostUrl) {
      redirectToSsoLogin()
    }
  }, [shouldAutoRedirectToSsoLogin, infraHostUrl])

  if (shouldAutoRedirectToSsoLogin) {
    return (
      <div className={classes.progress}>
        <Progress loading message="Redirecting to SSO login" />
      </div>
    )
  }

  return (
    <>
      {features?.experimental?.airgapped && (
        <ConfirmationDialog
          open={openForgetPassModal}
          title="Forgot your password?"
          text="Please contact your administrator to reset the password."
          cancelText="Close"
          customFooterActions={
            <Button
              variant="secondary"
              onClick={() => {
                setOpenForgetPassModal(false)
              }}
            >
              Close
            </Button>
          }
        />
      )}
      <FormPageContainer
        footer={
          customerTier !== CustomerTiers.OEM && !isOpenCloud() ? (
            <Text className={classes.paragraph} variant="caption3" color="textSecondary">
              By signing in, you agree to our{' '}
              <ExternalLink url="https://platform9.com/terms-conditions/" textVariant="caption3">
                Terms of Service
              </ExternalLink>
              . © 2014-{moment().year()} Platform9 Systems, Inc.
            </Text>
          ) : null
        }
      >
        <form
          data-testid={generateTestId('login', 'form')}
          className={classes.form}
          onSubmit={performLogin}
        >
          {ssoEnabled && (
            <Text
              data-testid={generateTestId('login', 'local', 'credentials')}
              className={classes.methodToggle}
              variant="caption1"
              onClick={() => {
                updateParams({
                  loginMethod:
                    loginMethod === LoginMethodTypes.Local
                      ? LoginMethodTypes.SSO
                      : LoginMethodTypes.Local,
                })
                if (loginMethod === LoginMethodTypes.SSO) {
                  window.history.replaceState({}, document.title, window.location.pathname)
                }
              }}
            >
              {loginMethod === LoginMethodTypes.Local ? 'Use SSO' : 'Use local credentials'}
            </Text>
          )}
          <Text variant="h3" className={classes.formTitle}>
            Sign In
          </Text>
          <div className={classes.fields}>
            {loginMethod === LoginMethodTypes.Local && (
              <>
                <Input
                  required
                  variant="dark"
                  id="email"
                  label="Email"
                  placeholder="Email"
                  onChange={updateValue('username')}
                />
                <div className={classes.passwordInputField}>
                  <Input
                    required
                    iconProps={{ placement: 'end', onClick: () => setShowPassword(!showPassword) }}
                    icon={showPassword ? 'eye' : 'eye-slash'}
                    variant="dark"
                    id="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    onChange={updateValue('password')}
                  />
                </div>
                <div className={classes.passwordInputField}>
                  <Input
                    variant="dark"
                    id="domain"
                    label="Domain"
                    type="text"
                    value={params.domain}
                    onChange={updateValue('domain')}
                  />
                  <Text className={classes.forgotPwd}>
                    <SimpleLink onClick={handleForgotPassword()} textVariant="caption2">
                      Forgot password?
                    </SimpleLink>
                  </Text>
                </div>
                {MFAcheckbox && (
                  <Input
                    required={MFAcheckbox}
                    variant="dark"
                    id="mfa"
                    label="MFA Code"
                    placeholder="MFA Code"
                    onChange={updateValue('mfa')}
                  />
                )}
                <Checkbox
                  checked={MFAcheckbox}
                  onChange={handleChangeBox('MFAcheckbox')}
                  label={
                    <Text component="div" variant="body2" className={classes.mfa}>
                      I have a
                      <Tooltip message="Multi-Factor Authentication">
                        <Text variant="body2">MFA</Text>
                      </Tooltip>
                      token.
                      <ExternalLink className={classes.mfaInfo} textVariant="body2" url={mfaDoc}>
                        More info
                      </ExternalLink>
                    </Text>
                  }
                />
              </>
            )}
            {loginFailed && (
              <Alert
                id="login-failed"
                variant="error"
                title="Login Failed."
                message={
                  loginMethod !== LoginMethodTypes.SSO
                    ? 'Please check your username, password, domain and try again'
                    : ''
                }
              />
            )}
            {loginMethod === LoginMethodTypes.SSO ? (
              <>
                <Input
                  variant="dark"
                  id="domain"
                  label="Domain"
                  type="text"
                  value={params.domain}
                  onChange={updateValue('domain')}
                />
                <Button
                  id="login-submit"
                  type="button"
                  loading={loading}
                  className={classes.signinButton}
                  onClick={redirectToSsoLogin}
                >
                  Sign In With SSO
                </Button>
              </>
            ) : (
              <Button
                id="login-submit"
                type="submit"
                loading={loading}
                className={classes.signinButton}
              >
                {loading ? 'Attempting login...' : 'Sign In'}
              </Button>
            )}
          </div>
        </form>
      </FormPageContainer>
    </>
  )
}

export default LoginPage
